import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type CallStatus = 'idle' | 'ringing' | 'active';

interface CallState {
  status: CallStatus;
  isVideoEnabled: boolean | null;
  channelId: string | null;
  fromUser: any | null;
  toUser: any | null;
  reason: string | null;
}

interface CallLink {
  active: boolean;
  fromCountry: string | null;
  toCountry: string | null;
  fromState: string | null;
  toState: string | null;
  fromCity: string | null;
  toCity: string | null;
  fromAvatar: string | null;
  toAvatar: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class OutgoingCallService {
  public outgoingCallSubject: any;
  public incomingCallRejectEventSubject: any;
  public incomingCallAcceptEventSubject: any;

  public currentlyOnCall: any;
  public callState$: BehaviorSubject<CallState>;
  public callLink$: BehaviorSubject<CallLink>;
  private localUserCountry: string | null = null;

  public setLocalUserCountry(country: string | null): void {
    this.localUserCountry = country;
  }

  constructor() {
    this.outgoingCallSubject = new BehaviorSubject(null);
    this.incomingCallRejectEventSubject = new BehaviorSubject(null);
    this.incomingCallAcceptEventSubject = new BehaviorSubject(null);

    this.callState$ = new BehaviorSubject<CallState>({
      status: 'idle',
      isVideoEnabled: null,
      channelId: null,
      fromUser: null,
      toUser: null,
      reason: null
    });
    this.callLink$ = new BehaviorSubject<CallLink>({
      active: false,
      fromCountry: null,
      toCountry: null,
      fromState: null,
      toState: null,
      fromCity: null,
      toCity: null,
      fromAvatar: null,
      toAvatar: null
    });
  }

  private getCountry(user: any): string | null {
    return user?.address?.country || user?.country || null;
  }

  private getState(user: any): string | null {
    return user?.address?.state || user?.state || null;
  }

  private getCity(user: any): string | null {
    return user?.address?.city || user?.city || null;
  }

  private getProfileImage(user: any): string | null {
    const profileImage = typeof user?.profileImage === 'string' ? user.profileImage.trim() : '';
    if (!profileImage) {
      return null;
    }
    // Allow everything, including assets/, to ensure we always show some image (default or custom)
    return profileImage;
  }

  private normalizeUser(user: any): any | null {
    if (!user) {
      return null;
    }

    // If user is just an ID (string), return a shell object
    if (typeof user === 'string') {
      return { _id: user, id: user, profileImage: null };
    }

    if (typeof user !== 'object') {
      return null;
    }

    return {
      ...user,
      profileImage: this.getProfileImage(user)
    };
  }

  public setRinging(fromUser: any, toUser: any, isVideoEnabled: boolean): void {
    const safeFromUser = this.normalizeUser(fromUser);
    const safeToUser = this.normalizeUser(toUser);

    console.log('setRinging fromUser:', JSON.stringify(safeFromUser));
    console.log('setRinging toUser:', JSON.stringify(safeToUser));
    console.log('setRinging fromCountry:', this.getCountry(safeFromUser));
    console.log('setRinging toCountry:', this.getCountry(safeToUser));

    this.currentlyOnCall = true;
    this.callState$.next({
      status: 'ringing',
      isVideoEnabled: !!isVideoEnabled,
      channelId: null,
      fromUser: safeFromUser,
      toUser: safeToUser,
      reason: null
    });
    this.callLink$.next({
      active: true,
      fromCountry: this.getCountry(safeFromUser) || this.localUserCountry,
      toCountry: this.getCountry(safeToUser),
      fromState: this.getState(safeFromUser),
      toState: this.getState(safeToUser),
      fromCity: this.getCity(safeFromUser),
      toCity: this.getCity(safeToUser),
      fromAvatar: safeFromUser?.profileImage || null,
      toAvatar: safeToUser?.profileImage || null
    });
  }

  public setActive(fromUser: any, toUser: any, channelId: string | null, isVideoEnabled: boolean): void {
    const safeFromUser = this.normalizeUser(fromUser);
    const safeToUser = this.normalizeUser(toUser);

    this.currentlyOnCall = true;
    this.callState$.next({
      status: 'active',
      isVideoEnabled: !!isVideoEnabled,
      channelId: channelId || null,
      fromUser: safeFromUser,
      toUser: safeToUser,
      reason: null
    });
    this.callLink$.next({
      active: true,
      fromCountry: this.getCountry(safeFromUser) || this.localUserCountry,
      toCountry: this.getCountry(safeToUser),
      fromState: this.getState(safeFromUser),
      toState: this.getState(safeToUser),
      fromCity: this.getCity(safeFromUser),
      toCity: this.getCity(safeToUser),
      fromAvatar: safeFromUser?.profileImage || null,
      toAvatar: safeToUser?.profileImage || null
    });
  }

  public clearCallState(reason: string | null = null): void {
    this.currentlyOnCall = false;
    this.callState$.next({
      status: 'idle',
      isVideoEnabled: null,
      channelId: null,
      fromUser: null,
      toUser: null,
      reason
    });
    this.callLink$.next({
      active: false,
      fromCountry: null,
      toCountry: null,
      fromState: null,
      toState: null,
      fromCity: null,
      toCity: null,
      fromAvatar: null,
      toAvatar: null
    });
  }

  public getCallLinkSnapshot(): CallLink {
    return this.callLink$.value;
  }
}
